import React, { FC, useContext, useMemo } from 'react';

// Constants
import styled from 'styled-components';

import aampeTheme from '../../../../../constants/aampeTheme';
import ICONS from '../../../../../constants/icons';

// Models
import { EditorContext } from '../../../../../context/EditorContext';

// Hooks
import useDatasetByIdQuery from '../../../../../hooks/datasets/useDatasetByIdQuery';

import {
    ElementKind,
    ImageVariantElement,
    ImageVariantEmailElement,
    SectionElement,
} from '../../../../../models/editor.model';
import { VariantType } from 'models/variant.model';

// Components
import TestFormulaCMSBlock from './elements/TestFormulaCMSBlock';
import TestFormulaVariantBlock from './elements/TestFormulaVariantBlock';

interface TestElementSelectorBodyProps {
    formulaSection:
        | SectionElement
        | ImageVariantElement
        | ImageVariantEmailElement;
}

const TestFormulaElementSelectorBody: FC<TestElementSelectorBodyProps> = ({
    formulaSection,
}) => {
    const {
        editorDetails: { formula },
    } = useContext(EditorContext);

    const { data: dataset } = useDatasetByIdQuery(Number(formula?.dataset_id));

    const section = formulaSection;
    const isEmailImageVariant = useMemo<boolean>(() => {
        return (
            section.kind === ElementKind.section &&
            'variantType' in section &&
            section.variantType === VariantType.Image
        );
    }, [section]);

    return (
        <SectionBody>
            {isEmailImageVariant && (
                <TestFormulaVariantBlock
                    component={section as ImageVariantEmailElement}
                    key={`variant-${
                        (section as ImageVariantEmailElement)?.id ?? ''
                    }`}
                />
            )}

            {section.kind !== ElementKind.image &&
                section.children.map((child, i) => {
                    // VARIANT
                    if ('kind' in child && child.kind === ElementKind.variant) {
                        return (
                            <TestFormulaVariantBlock
                                component={child}
                                key={`variant-${i}`}
                            />
                        );
                    }

                    // DATASET
                    if (
                        'kind' in child &&
                        child.kind === ElementKind.dataset &&
                        dataset &&
                        dataset.fields
                    ) {
                        const field = dataset.fields.find(
                            (f) => f.id === child.datasetField,
                        );
                        return (
                            <DatasetRow key={`dataset-${i}`}>
                                <StyledDatasetIcon /> {field?.name}
                            </DatasetRow>
                        );
                    }

                    // ATTRIBUTE PLACEHOLDER
                    if (
                        'kind' in child &&
                        child.kind === ElementKind.attributePlaceholder
                    ) {
                        return (
                            <Attribute key={i}>
                                <StyledAttributeIcon /> {child.attributeName}
                            </Attribute>
                        );
                    }

                    // CMS PLACEHOLDER
                    if (
                        'kind' in child &&
                        child.kind === ElementKind.cms_placeholder
                    ) {
                        return (
                            <TestFormulaCMSBlock
                                cmsElement={child}
                                key={`cms-${i}`}
                            />
                        );
                    }

                    // PLAIN TEXT
                    if (
                        'text' in child &&
                        child.text.trim() &&
                        !isEmailImageVariant
                    ) {
                        return (
                            <Text key={i}>{'text' in child && child.text}</Text>
                        );
                    }
                })}

            {section.kind === ElementKind.image &&
                ('datasetField' in section ? (
                    <DatasetRow>
                        {'datasetField' in section && <StyledDatasetIcon />}
                        <TruncatedText>
                            {(section as ImageVariantElement).children[0].text}
                        </TruncatedText>
                    </DatasetRow>
                ) : 'variantType' in section ? (
                    <TestFormulaVariantBlock component={section} />
                ) : (
                    <AlternateImage
                        src={(section as ImageVariantElement).children[0].text}
                    />
                ))}
        </SectionBody>
    );
};

export default TestFormulaElementSelectorBody;

const SectionBody = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
    gap: aampeTheme.spacing4(3),
    alignItems: 'center',
});

const DatasetRow = styled.span({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
});

const Attribute = styled.span({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(1),
});

const Text = styled.span({
    fontSize: aampeTheme.size.font.default,
    lineHeight: aampeTheme.size.lineHeight.default,
    color: aampeTheme.colors.textAccessibleWG01,
});

const StyledDatasetIcon = styled(ICONS.datasets)({
    width: '20px',
    height: '20px',
    color: aampeTheme.colors.textAccessibleWG01,
});

const StyledAttributeIcon = styled(ICONS.attributePlaceholder)({
    width: '20px',
    height: '20px',
    color: aampeTheme.colors.textAccessibleWG01,
});

const TruncatedText = styled.span({
    display: 'inline-block',
    maxWidth: '100px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
});

const AlternateImage = styled.img({
    borderRadius: aampeTheme.borderRadius.default,
    height: 64,
    width: 64,
    objectFit: 'contain',
});
